import { FC, useState, useMemo, useEffect, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Checkbox,
  TextField,
  Autocomplete,
  ToggleButtonGroup,
  ToggleButton,
  Grid,
  Chip,
  Alert,
  AlertTitle,
  CircularProgress,
  Divider,
  Card,
  CardContent,
  Stack,
  IconButton,
  Tooltip,
  Link,
  Popover,
} from '@mui/material';
import {
  Warehouse as BarnIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  Close as CloseIcon,
  MedicalServices as ProcedureIcon,
  Vaccines as VaccineIcon,
  Medication as MedicationIcon,
  GroupAdd as GroupAddIcon,
} from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { useSnackbar } from 'notistack';
import api, { Horse, Owner } from '../../utils/api';
import { formatBreed } from '../../utils/breed';
import { serviceCatalogService, ServiceCatalogItem } from '../../services/serviceCatalogService';
import { barnsService, BarnOwnerLink } from '../../services/barnsService';
import AddHorsesToBarnDialog from '../Barns/AddHorsesToBarnDialog';
import {
  withOfflineFallback,
  skipWhenOffline,
  readDistinctLocations,
  readHorsesPage,
  readVeterinarians,
  OfflineNotice,
  useIsOffline,
} from '../../offline';
import { format, addYears } from 'date-fns';
import { ymdToNoonUtcIso } from '../../utils/dateUtils';
import { formatQuantity } from '../../utils/quantity';
import QuantityField from '../../components/QuantityField';
import DeductFromLocationSelect from '../../components/Inventory/DeductFromLocationSelect';
import DeaBottleSelect from '../../components/DEALogs/DeaBottleSelect';
import BarnVaccineLotsDialog from '../../components/Vaccination/BarnVaccineLotsDialog';
import { useRBAC } from '../../auth/useRBAC';
import { barnOperationsControls } from '../../auth/pageControls';
import {
  AssignedLineItem,
  DRAFT_STORAGE_KEY,
  LineItemData,
  OperationDraft,
  ServiceType,
  SharedFormData,
  buildItemPayload,
  vaccineLinesOf,
  withVaccineLots,
  estimateOperationTotal,
  getBilledQty,
  getLineTotal,
  initialLineItem,
  makeInitialSharedData,
  materializePerHorseItems,
  parseOperationDraft,
  pruneItemHorseIds,
} from './barnOperationsItems';

interface Veterinarian {
  id: string;
  name: string;
  firstName?: string;
  lastName?: string;
  professionalTitle?: string;
}

interface BarnOperationResult {
  horseId: string;
  horseName: string;
  clinicalItemIds: string[];
  invoiceIds: string[];
  invoiceNumbers: string[];
  clientNames: string[];
  amount: number;
  status: 'success' | 'error';
  error?: string;
}

interface BarnOperationResponse {
  results: BarnOperationResult[];
  summary: {
    totalHorses: number;
    successCount: number;
    errorCount: number;
    totalAmount: number;
    totalInvoices: number;
  };
}

const SERVICE_TYPE_LABELS: Record<ServiceType, string> = {
  procedure: 'Procedure',
  vaccination: 'Vaccination',
  medication: 'Medication',
};

// In-progress work is drafted to localStorage so navigating away (e.g. to a
// patient chart) doesn't lose it. Cleared on submit success and on Reset.
// Shape/migration live in barnOperationsItems.ts (v2 = per-item horseIds).
const readOperationDraft = (): OperationDraft | null => {
  try {
    return parseOperationDraft(localStorage.getItem(DRAFT_STORAGE_KEY));
  } catch {
    return null;
  }
};

const clearOperationDraft = () => {
  try {
    localStorage.removeItem(DRAFT_STORAGE_KEY);
  } catch {
    /* best-effort */
  }
};

// Vaccination "Next Due Date" defaults to one year after the administer
// (Service) date. Parse as local midnight so the rendered date doesn't slip a
// day across time zones.
const oneYearAfter = (dateStr: string): string => {
  if (!dateStr) return '';
  const parsed = new Date(`${dateStr}T00:00:00`);
  if (Number.isNaN(parsed.getTime())) return '';
  return format(addYears(parsed, 1), 'yyyy-MM-dd');
};

const ServiceTypeIcon: FC<{ type: ServiceType }> = ({ type }) => {
  const sx = { fontSize: '1rem' };
  if (type === 'vaccination') return <VaccineIcon sx={sx} />;
  if (type === 'medication') return <MedicationIcon sx={sx} />;
  return <ProcedureIcon sx={sx} />;
};

const BarnOperations: FC = () => {
  const { enqueueSnackbar } = useSnackbar();
  const queryClient = useQueryClient();
  const isOffline = useIsOffline();
  const navigate = useNavigate();
  // The screen READS barns (view:patients, which is what <ScreenRoute> let this person in on) and
  // WRITES invoices: billing.controller.ts `barnOperations` -> manage:charges (INV-108). Two
  // unrelated slugs, so the submit asks its own rather than riding on the read.
  const rbac = useRBAC();
  const can = barnOperationsControls(rbac);

  // Deep link from the Barns page: /barn-operations?barn=<name> preselects
  // the barn and (one-shot) auto-checks all of its horses.
  const [searchParams] = useSearchParams();
  const barnFromUrl = searchParams.get('barn');
  // Restore an in-progress draft. An explicit ?barn= that differs from the
  // draft's barn wins — that's a deliberate fresh start on another barn.
  const [restoredDraft] = useState<OperationDraft | null>(() => {
    const draft = readOperationDraft();
    if (draft && barnFromUrl && draft.selectedBarn !== barnFromUrl) return null;
    return draft;
  });
  const [selectedBarn, setSelectedBarn] = useState<string | null>(
    barnFromUrl || restoredDraft?.selectedBarn || null,
  );
  const [selectedHorseIds, setSelectedHorseIds] = useState<string[]>(
    restoredDraft?.selectedHorseIds ?? [],
  );
  // A restored draft already carries the user's horse selection — suppress the
  // one-shot auto-select-all so it doesn't clobber deliberate deselections.
  const autoSelectedForBarn = useRef<string | null>(restoredDraft ? barnFromUrl : null);
  const [addHorsesOpen, setAddHorsesOpen] = useState(false);
  const [items, setItems] = useState<AssignedLineItem[]>(restoredDraft?.items ?? []);
  const [currentItem, setCurrentItem] = useState<LineItemData>(
    restoredDraft?.currentItem ?? initialLineItem,
  );
  const [sharedData, setSharedData] = useState<SharedFormData>(
    restoredDraft?.sharedData ?? makeInitialSharedData(),
  );
  // Per-item "Applies to" popover editor (index into `items` + anchor).
  const [editingItemIndex, setEditingItemIndex] = useState<number | null>(null);
  const [itemHorsesAnchor, setItemHorsesAnchor] = useState<HTMLElement | null>(null);
  // Run-level "Deduct stock from" ('' = Automatic — server infers, ADR-0013).
  const [dispenseLocationId, setDispenseLocationId] = useState<string>('');
  const [operationResults, setOperationResults] = useState<BarnOperationResponse | null>(null);

  const { data: locations = [], isLoading: isLoadingLocations } = useQuery<string[]>(
    ['horse-locations'],
    withOfflineFallback(
      async () => {
        const response = await api.get('/horses/locations');
        return response.data;
      },
      readDistinctLocations,
    ),
  );

  const { data: horses = [], isLoading: isLoadingHorses } = useQuery<Horse[]>(
    ['horses', { location: selectedBarn }],
    withOfflineFallback(
      async () => {
        const response = await api.get('/horses', { params: { location: selectedBarn } });
        return response.data;
      },
      // GET /horses returns a bare array — unwrap the paginated replica read to match.
      () => readHorsesPage({ page: 1, limit: 1000, location: selectedBarn! }).then((p) => p.data as Horse[]),
    ),
    { enabled: !!selectedBarn },
  );

  // Resolve the selected location string to its Barn record so this page can
  // offer Add-horses and clickable owner links (ownerLinks carry clientIds,
  // which the horses list's owners JSON doesn't). Offline (or on an unmatched
  // string) these enrichments quietly disappear — the legacy flow still works.
  const { data: barnRecords = [] } = useQuery(
    ['barns'],
    () => barnsService.list(),
    { enabled: !isOffline, staleTime: 60_000 },
  );
  const selectedBarnRecord = useMemo(
    () =>
      selectedBarn
        ? barnRecords.find((b) => b.name.toLowerCase() === selectedBarn.toLowerCase()) ?? null
        : null,
    [barnRecords, selectedBarn],
  );

  const { data: selectedBarnDetail } = useQuery(
    ['barn', selectedBarnRecord?.id],
    () => barnsService.get(selectedBarnRecord!.id),
    { enabled: !!selectedBarnRecord && !isOffline },
  );
  const ownerLinksByHorse = useMemo(() => {
    const map = new Map<string, BarnOwnerLink[]>();
    for (const rosterHorse of selectedBarnDetail?.horses ?? []) {
      if (rosterHorse.ownerLinks.length > 0) map.set(rosterHorse.id, rosterHorse.ownerLinks);
    }
    return map;
  }, [selectedBarnDetail]);

  // One-shot: when arriving via ?barn= and its horses finish loading, select
  // them all. The ref guard keeps later manual deselections sticky.
  useEffect(() => {
    if (!barnFromUrl || selectedBarn !== barnFromUrl) return;
    if (autoSelectedForBarn.current === barnFromUrl) return;
    if (horses.length === 0) return;
    autoSelectedForBarn.current = barnFromUrl;
    setSelectedHorseIds(horses.map((h) => h.id));
  }, [barnFromUrl, selectedBarn, horses]);

  // A restored selection can reference horses since moved out of the barn —
  // prune them once the roster loads so the preview and payload stay honest.
  // Item assignments are pruned too (a departed horse must not stay billable
  // through a stale draft); pruneItemHorseIds returns the same reference when
  // nothing changed, so the setter no-ops.
  useEffect(() => {
    if (!selectedBarn || isLoadingHorses || horses.length === 0) return;
    const rosterIds = new Set(horses.map((h) => h.id));
    setSelectedHorseIds((prev) => {
      const pruned = prev.filter((id) => rosterIds.has(id));
      return pruned.length === prev.length ? prev : pruned;
    });
    setItems((prev) => pruneItemHorseIds(prev, rosterIds));
  }, [selectedBarn, isLoadingHorses, horses]);

  // Draft the in-progress work on every change; drop the draft once the page
  // is back to a blank slate.
  useEffect(() => {
    const hasWork =
      !!selectedBarn ||
      selectedHorseIds.length > 0 ||
      items.length > 0 ||
      currentItem.serviceName.trim() !== '';
    try {
      if (!hasWork) {
        localStorage.removeItem(DRAFT_STORAGE_KEY);
        return;
      }
      const draft: OperationDraft = {
        version: 2,
        selectedBarn,
        selectedHorseIds,
        items,
        currentItem,
        sharedData,
      };
      localStorage.setItem(DRAFT_STORAGE_KEY, JSON.stringify(draft));
    } catch {
      /* storage unavailable — drafts are best-effort */
    }
  }, [selectedBarn, selectedHorseIds, items, currentItem, sharedData]);

  const { data: catalogItems = [] } = useQuery<ServiceCatalogItem[]>(
    ['service-catalog', currentItem.serviceType],
    () => serviceCatalogService.getAll({ category: currentItem.serviceType, isActive: true }),
    { enabled: !!currentItem.serviceType },
  );

  const { data: veterinarians = [] } = useQuery<Veterinarian[]>(
    ['veterinarians'],
    withOfflineFallback(
      async () => {
        const response = await api.get('/users/veterinarians');
        return response.data;
      },
      () => readVeterinarians() as Promise<Veterinarian[]>,
    ),
  );

  const barnOperationMutation = useMutation(
    async (payload: Record<string, unknown>) => {
      const response = await api.post('/billing/barn-operations', payload);
      return response.data as BarnOperationResponse;
    },
    {
      onSuccess: (data) => {
        setOperationResults(data);
        // The work is now invoiced — restoring it later would risk applying
        // (and billing) the same services twice. Reset the line items so the
        // user can immediately start another round on the same barn/horses.
        clearOperationDraft();
        setItems([]);
        setCurrentItem(initialLineItem);
        queryClient.invalidateQueries(['invoices']);
        // The Invoicing page's header tiles cache the full ClinicInvoiceStats
        // under ['billing-stats'] with staleTime 60s, a key the 'invoices'
        // prefix does NOT match — so without this the clinic saw the new rows
        // (and the Dashboard's AR tile, which IS under that prefix) move while
        // Outstanding / Past due stayed put, across a fresh /billing navigation.
        queryClient.invalidateQueries(['billing-stats']);
        if (data.summary.errorCount === 0) {
          enqueueSnackbar(
            `Successfully applied to ${data.summary.successCount} horse${data.summary.successCount !== 1 ? 's' : ''}`,
            { variant: 'success' },
          );
        } else {
          enqueueSnackbar(
            `${data.summary.successCount} succeeded, ${data.summary.errorCount} failed`,
            { variant: 'warning' },
          );
        }
      },
      onError: (error: any) => {
        enqueueSnackbar(
          error.response?.data?.message || 'Barn operation failed',
          { variant: 'error' },
        );
      },
    },
  );

  const handleBarnChange = (_: any, value: string | null) => {
    setSelectedBarn(value);
    setSelectedHorseIds([]);
    // Items are bound to the previous barn's horses — a service list rarely
    // survives a barn switch, and stale assignments would bill ghosts.
    setItems([]);
    setCurrentItem(initialLineItem);
    setOperationResults(null);
  };

  const handleSelectAll = (checked: boolean) => {
    setSelectedHorseIds(checked ? horses.map((h) => h.id) : []);
  };

  const handleSelectHorse = (horseId: string, checked: boolean) => {
    setSelectedHorseIds((prev) =>
      checked ? [...prev, horseId] : prev.filter((id) => id !== horseId),
    );
  };

  const handleServiceTypeChange = (_: any, value: ServiceType | null) => {
    if (value) {
      setCurrentItem((prev) => ({
        ...prev,
        serviceType: value,
        serviceName: '',
        unitPrice: '',
        category: '',
        // Vaccinations default their next-due date to a year out; clear it
        // again when switching away so procedures/medications don't carry it.
        nextDueDate:
          value === 'vaccination'
            ? prev.nextDueDate || oneYearAfter(sharedData.date)
            : '',
      }));
    }
  };

  // Keep an auto-filled vaccination due date in sync with the Service Date,
  // but never clobber a date the user typed themselves.
  const handleServiceDateChange = (value: string) => {
    setCurrentItem((prev) => {
      if (prev.serviceType !== 'vaccination') return prev;
      const prevAuto = oneYearAfter(sharedData.date);
      if (prev.nextDueDate === '' || prev.nextDueDate === prevAuto) {
        return { ...prev, nextDueDate: oneYearAfter(value) };
      }
      return prev;
    });
    setSharedData((prev) => ({ ...prev, date: value }));
  };

  const handleCatalogSelect = (_: any, value: string | ServiceCatalogItem | null) => {
    if (value && typeof value !== 'string') {
      setCurrentItem((prev) => ({
        ...prev,
        serviceName: value.name,
        unitPrice: value.basePrice,
        category: value.category,
      }));
    } else if (typeof value === 'string') {
      setCurrentItem((prev) => ({ ...prev, serviceName: value }));
    } else {
      setCurrentItem((prev) => ({
        ...prev,
        serviceName: '',
        unitPrice: '',
        category: '',
      }));
    }
  };

  // Adding binds the service to the CURRENTLY checked horses (snapshot) — the
  // checkboxes pick targets for the next item, not a global bill list.
  const canAddItem = currentItem.serviceName.trim() !== '' && selectedHorseIds.length > 0;

  const handleAddItem = () => {
    if (!canAddItem) return;
    setItems((prev) => [...prev, { ...currentItem, horseIds: [...selectedHorseIds] }]);
    setCurrentItem(initialLineItem);
  };

  const handleRemoveItem = (index: number) => {
    setItems((prev) => prev.filter((_, i) => i !== index));
  };

  const handleToggleItemHorse = (index: number, horseId: string, checked: boolean) => {
    setItems((prev) =>
      prev.map((it, i) =>
        i === index
          ? {
              ...it,
              horseIds: checked
                ? [...it.horseIds, horseId]
                : it.horseIds.filter((id) => id !== horseId),
            }
          : it,
      ),
    );
  };

  const handleSetItemHorses = (index: number, horseIds: string[]) => {
    setItems((prev) => prev.map((it, i) => (i === index ? { ...it, horseIds } : it)));
  };

  // Materialize per-horse assignments once — drives the preview, the totals,
  // the submit payload, and the invoice count, so they can't disagree.
  const materialized = useMemo(
    () => materializePerHorseItems(items, horses),
    [items, horses],
  );

  // A run with vaccines asks for their lots first; the payload waits here (spec §18, ADR-0047).
  const [lotStepPayload, setLotStepPayload] = useState<Record<string, any> | null>(null);
  const vaccineLines = useMemo(() => vaccineLinesOf(materialized, horses), [materialized, horses]);

  const handleSubmit = () => {
    if (materialized.length === 0) return;

    // Creates invoices server-side — online-only by policy, never queued.
    if (!skipWhenOffline()) {
      enqueueSnackbar(
        "You're offline — barn billing creates invoices and needs a connection.",
        { variant: 'info' },
      );
      return;
    }

    // perHorseItems wins over the legacy horseIds+items shape server-side
    // (BarnOperationsDto @ValidateIf) — each horse gets exactly its items.
    const payload: Record<string, unknown> = {
      perHorseItems: materialized.map((entry) => ({
        horseId: entry.horseId,
        items: entry.items.map((item) => buildItemPayload(item, dispenseLocationId)),
      })),
      // W1/W2 (DATE-KINDS §2.5): both of these are CALENDAR days picked in a
      // `type="date"` field, and both used to be posted as
      // `new Date(yyyy-MM-dd).toISOString()` = UTC MIDNIGHT — while the invoice
      // form two files over posts the same two fields at NOON UTC. One column
      // (`Invoice.dueDate`, `Invoice.serviceDate`), two anchors, decided by which
      // screen created the invoice. Noon is the sanctioned anchor (it matches
      // `anchorCalendarDayUtc` on every server-side writer) and is the only one
      // that is day-stable across the UTC-12..UTC+11:59 band (every clinic we have).
      date: ymdToNoonUtcIso(sharedData.date),
      createInvoices: true,
      dueDate: ymdToNoonUtcIso(sharedData.dueDate),
    };

    if (sharedData.performedBy) payload.performedBy = sharedData.performedBy;
    if (sharedData.invoiceDescription) payload.invoiceDescription = sharedData.invoiceDescription;

    if (vaccineLines.length > 0) {
      setLotStepPayload(payload);
      return;
    }
    barnOperationMutation.mutate(payload);
  };

  const handleReset = () => {
    setSelectedHorseIds([]);
    setItems([]);
    setCurrentItem(initialLineItem);
    setSharedData(makeInitialSharedData());
    setOperationResults(null);
    clearOperationDraft();
  };

  const estimatedTotal = useMemo(
    () => estimateOperationTotal(items, horses),
    [items, horses],
  );

  // An item whose horses all left the barn (or were unassigned by hand) blocks
  // submit rather than silently dropping a line the user typed.
  const hasOrphanedItem = items.some((item) => item.horseIds.length === 0);

  const canSubmit =
    items.length > 0 &&
    materialized.length > 0 &&
    !hasOrphanedItem &&
    sharedData.date !== '' &&
    !barnOperationMutation.isLoading;

  const getOwnerNames = (horse: Horse): string => {
    if (!horse.owners || horse.owners.length === 0) return 'No owner';
    return horse.owners.map((o: Owner) => o.name).join(', ');
  };

  return (
    <Box>
      {/* Barn Selector */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Stack direction="row" alignItems="center" spacing={1} mb={2}>
          <BarnIcon sx={{ color: 'primary.main' }} />
          <Typography variant="h6" fontWeight={600}>
            Select Barn
          </Typography>
        </Stack>
        <Autocomplete
          value={selectedBarn}
          onChange={handleBarnChange}
          options={locations}
          loading={isLoadingLocations}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Barn / Location"
              placeholder="Choose a barn to see its horses"
              size="small"
            />
          )}
          sx={{ maxWidth: 400 }}
        />
        {selectedBarn && (
          <Typography variant="body2" color="text.secondary" mt={1}>
            {isLoadingHorses
              ? 'Loading horses...'
              : `${horses.length} horse${horses.length !== 1 ? 's' : ''} in ${selectedBarn}`}
          </Typography>
        )}
      </Paper>

      {/* Horse Selection */}
      {selectedBarn && horses.length > 0 && (
        <Paper sx={{ p: 3, mb: 3 }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
            <Stack direction="row" alignItems="center" gap={1}>
              <Typography variant="h6" fontWeight={600}>
                Select Horses
              </Typography>
              {selectedHorseIds.length > 0 && (
                <Chip
                  label={`${selectedHorseIds.length} selected`}
                  color="primary"
                  size="small"
                />
              )}
            </Stack>
            {selectedBarnRecord && (
              <Button
                size="small"
                startIcon={<GroupAddIcon />}
                onClick={() => setAddHorsesOpen(true)}
              >
                Add horses to this barn
              </Button>
            )}
          </Stack>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: -1.5, mb: 1.5 }}>
            Checked horses receive the next service you add. Each added service remembers its own
            horses — change a service's horses later from the Line Items list.
          </Typography>
          <TableContainer sx={{ maxHeight: 350 }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox">
                    <Checkbox
                      checked={
                        selectedHorseIds.length === horses.length && horses.length > 0
                      }
                      indeterminate={
                        selectedHorseIds.length > 0 &&
                        selectedHorseIds.length < horses.length
                      }
                      onChange={(e) => handleSelectAll(e.target.checked)}
                    />
                  </TableCell>
                  <TableCell>Horse Name</TableCell>
                  <TableCell>Breed</TableCell>
                  <TableCell>Owner(s)</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {horses.map((horse) => (
                  <TableRow
                    key={horse.id}
                    hover
                    onClick={() =>
                      handleSelectHorse(
                        horse.id,
                        !selectedHorseIds.includes(horse.id),
                      )
                    }
                    sx={{ cursor: 'pointer' }}
                  >
                    <TableCell padding="checkbox">
                      <Checkbox checked={selectedHorseIds.includes(horse.id)} />
                    </TableCell>
                    <TableCell>
                      <Link
                        component="button"
                        underline="hover"
                        fontWeight={500}
                        variant="body2"
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/patients/${horse.id}`);
                        }}
                      >
                        {horse.name}
                      </Link>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" color="text.secondary">
                        {formatBreed(horse)}
                      </Typography>
                    </TableCell>
                    <TableCell onClick={(e) => e.stopPropagation()} sx={{ cursor: 'default' }}>
                      {ownerLinksByHorse.has(horse.id) ? (
                        <Typography variant="body2" color="text.secondary">
                          {ownerLinksByHorse.get(horse.id)!.map((link, i) => (
                            <span key={link.clientId}>
                              {i > 0 && ', '}
                              <Link
                                component="button"
                                underline="hover"
                                variant="body2"
                                onClick={() =>
                                  navigate('/clients', {
                                    state: { selectedClientId: link.clientId },
                                  })
                                }
                              >
                                {link.name}
                              </Link>
                            </span>
                          ))}
                        </Typography>
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          {getOwnerNames(horse)}
                        </Typography>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      {selectedBarn && horses.length === 0 && !isLoadingHorses && (
        <Alert
          severity="info"
          sx={{ mb: 3 }}
          action={
            selectedBarnRecord ? (
              <Button
                color="inherit"
                size="small"
                startIcon={<GroupAddIcon />}
                onClick={() => setAddHorsesOpen(true)}
              >
                Add horses
              </Button>
            ) : undefined
          }
        >
          No horses found in &quot;{selectedBarn}&quot;. Make sure horses have their
          location set to this barn.
        </Alert>
      )}

      {/* Add Service Item — visible whenever a barn with horses is chosen, so
          items already added never strand behind a hidden panel when the user
          unchecks every horse. */}
      {selectedBarn && horses.length > 0 && (
        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" fontWeight={600} mb={2}>
            Add Service
          </Typography>

          <ToggleButtonGroup
            exclusive
            value={currentItem.serviceType}
            onChange={handleServiceTypeChange}
            size="small"
            sx={{ mb: 3 }}
          >
            <ToggleButton value="procedure">Procedure</ToggleButton>
            <ToggleButton value="vaccination">Vaccination</ToggleButton>
            <ToggleButton value="medication">Medication</ToggleButton>
          </ToggleButtonGroup>

          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <Autocomplete
                key={currentItem.serviceType}
                options={catalogItems}
                getOptionLabel={(option) =>
                  typeof option === 'string'
                    ? option
                    : `${option.name} — $${option.basePrice.toFixed(2)}`
                }
                onChange={handleCatalogSelect}
                freeSolo
                onInputChange={(_, value, reason) => {
                  if (reason === 'input') {
                    setCurrentItem((prev) => ({ ...prev, serviceName: value }));
                  }
                }}
                inputValue={currentItem.serviceName}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Service Name"
                    placeholder="Search catalog or type a name"
                    size="small"
                    required
                  />
                )}
              />
            </Grid>

            <Grid item xs={12} sm={3}>
              <TextField
                label="Unit Price ($)"
                type="number"
                size="small"
                fullWidth
                value={currentItem.unitPrice}
                onChange={(e) =>
                  setCurrentItem((prev) => ({
                    ...prev,
                    unitPrice: e.target.value === '' ? '' : Number(e.target.value),
                  }))
                }
                inputProps={{ min: 0, step: 0.01 }}
              />
            </Grid>

            <Grid item xs={12} sm={3}>
              <TextField
                label="Notes"
                size="small"
                fullWidth
                value={currentItem.notes}
                onChange={(e) =>
                  setCurrentItem((prev) => ({ ...prev, notes: e.target.value }))
                }
              />
            </Grid>

            {/* DEA bottle prompt — meds/vaccines only; renders when the
              * typed name single-matches the CS registry (ADR-0014). */}
            {currentItem.serviceType !== 'procedure' && (
              <Grid item xs={12} sm={6}>
                <DeaBottleSelect
                  drugName={currentItem.serviceName}
                  value={currentItem.deaLotId}
                  onChange={(lotId) =>
                    setCurrentItem((prev) => ({ ...prev, deaLotId: lotId }))
                  }
                  quantity={
                    currentItem.serviceType === 'medication'
                      ? currentItem.quantity === ''
                        ? undefined
                        : Number(currentItem.quantity)
                      : 1 // vaccinations dispense one dose (server `?? 1`)
                  }
                  margin="none"
                />
              </Grid>
            )}

            {currentItem.serviceType === 'vaccination' && (
              <Grid item xs={12} sm={4}>
                <TextField
                  label="Next Due Date"
                  type="date"
                  size="small"
                  fullWidth
                  value={currentItem.nextDueDate}
                  onChange={(e) =>
                    setCurrentItem((prev) => ({
                      ...prev,
                      nextDueDate: e.target.value,
                    }))
                  }
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
            )}

            {currentItem.serviceType === 'medication' && (
              <>
                <Grid item xs={12}>
                  <Divider sx={{ my: 1 }}>
                    <Typography variant="caption" color="text.secondary">
                      Medication Details
                    </Typography>
                  </Divider>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField
                    label="Dosage"
                    size="small"
                    fullWidth
                    placeholder="50mg, 10ml, etc."
                    value={currentItem.dosage}
                    onChange={(e) =>
                      setCurrentItem((prev) => ({
                        ...prev,
                        dosage: e.target.value,
                      }))
                    }
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  {/* Hundredths (ADR-0038); an empty box bills one (getBilledQty). */}
                  <QuantityField
                    label="Quantity"
                    size="small"
                    fullWidth
                    value={currentItem.quantity === '' ? null : currentItem.quantity}
                    onChange={(next) =>
                      setCurrentItem((prev) => ({
                        ...prev,
                        quantity: next ?? '',
                      }))
                    }
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField
                    label="End Date"
                    type="date"
                    size="small"
                    fullWidth
                    value={currentItem.endDate}
                    onChange={(e) =>
                      setCurrentItem((prev) => ({
                        ...prev,
                        endDate: e.target.value,
                      }))
                    }
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    label="Instructions"
                    size="small"
                    fullWidth
                    multiline
                    minRows={1}
                    maxRows={3}
                    placeholder="e.g., Give 1 tablet BID PO for 7 days"
                    value={currentItem.instructions}
                    onChange={(e) =>
                      setCurrentItem((prev) => ({
                        ...prev,
                        instructions: e.target.value,
                      }))
                    }
                  />
                </Grid>
              </>
            )}
          </Grid>

          <Box mt={2}>
            <Button
              variant="outlined"
              startIcon={<AddIcon />}
              onClick={handleAddItem}
              disabled={!canAddItem}
            >
              {selectedHorseIds.length > 0
                ? `Add for ${selectedHorseIds.length} horse${selectedHorseIds.length !== 1 ? 's' : ''}`
                : 'Add to List'}
            </Button>
            {selectedHorseIds.length === 0 && (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.75 }}>
                Select at least one horse above — the service will be added for the currently
                checked horses.
              </Typography>
            )}
          </Box>
        </Paper>
      )}

      {/* Added Items List */}
      {items.length > 0 && (
        <Paper sx={{ p: 3, mb: 3 }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography variant="h6" fontWeight={600}>
              Line Items
            </Typography>
            <Chip label={`${items.length} item${items.length !== 1 ? 's' : ''}`} size="small" />
          </Stack>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Type</TableCell>
                  <TableCell>Service</TableCell>
                  <TableCell>Applies to</TableCell>
                  <TableCell align="right">Unit Price</TableCell>
                  <TableCell align="right">Qty</TableCell>
                  <TableCell align="right">Line Total</TableCell>
                  <TableCell padding="checkbox" />
                </TableRow>
              </TableHead>
              <TableBody>
                {items.map((item, index) => {
                  const qty = getBilledQty(item);
                  const price = typeof item.unitPrice === 'number' ? item.unitPrice : 0;
                  const assignedNames = item.horseIds
                    .map((id) => horses.find((h) => h.id === id)?.name)
                    .filter(Boolean) as string[];
                  const nameSummary =
                    assignedNames.length <= 8
                      ? assignedNames.join(', ')
                      : `${assignedNames.slice(0, 8).join(', ')} +${assignedNames.length - 8} more`;
                  return (
                    <TableRow key={index}>
                      <TableCell>
                        <Stack direction="row" alignItems="center" spacing={0.5}>
                          <ServiceTypeIcon type={item.serviceType} />
                          <Typography variant="body2">
                            {SERVICE_TYPE_LABELS[item.serviceType]}
                          </Typography>
                        </Stack>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight={500}>
                          {item.serviceName}
                        </Typography>
                        {item.notes && (
                          <Typography variant="caption" color="text.secondary">
                            {item.notes}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Tooltip
                          title={
                            item.horseIds.length === 0
                              ? 'No horses — assign at least one or remove this line'
                              : nameSummary
                          }
                          arrow
                        >
                          <Chip
                            label={`${item.horseIds.length} of ${horses.length}`}
                            size="small"
                            color={item.horseIds.length === 0 ? 'warning' : 'default'}
                            onClick={(e) => {
                              setEditingItemIndex(index);
                              setItemHorsesAnchor(e.currentTarget);
                            }}
                            sx={{ cursor: 'pointer' }}
                          />
                        </Tooltip>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2">
                          {price > 0 ? `$${price.toFixed(2)}` : '—'}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2">{formatQuantity(qty)}</Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" fontWeight={500}>
                          {price > 0
                            ? `$${(getLineTotal(item) * item.horseIds.length).toFixed(2)}`
                            : '—'}
                        </Typography>
                        {price > 0 && item.horseIds.length > 1 && (
                          <Typography variant="caption" color="text.secondary" display="block">
                            ${getLineTotal(item).toFixed(2)} × {item.horseIds.length} horses
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell padding="checkbox">
                        <Tooltip title="Remove">
                          <IconButton
                            size="small"
                            onClick={() => handleRemoveItem(index)}
                            color="error"
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
          {hasOrphanedItem && (
            <Alert severity="warning" sx={{ mt: 1.5 }}>
              A line item has no horses assigned — assign at least one (click its "0 of{' '}
              {horses.length}" chip) or remove it before applying.
            </Alert>
          )}
        </Paper>
      )}

      {/* Per-item "Applies to" editor */}
      <Popover
        open={editingItemIndex !== null && Boolean(itemHorsesAnchor)}
        anchorEl={itemHorsesAnchor}
        onClose={() => {
          setEditingItemIndex(null);
          setItemHorsesAnchor(null);
        }}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        {editingItemIndex !== null && items[editingItemIndex] && (
          <Box sx={{ p: 1.5, minWidth: 240, maxHeight: 360, overflowY: 'auto' }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" mb={0.5}>
              <Typography variant="subtitle2" sx={{ pr: 2 }}>
                {items[editingItemIndex].serviceName}
              </Typography>
              <Stack direction="row" spacing={0.5}>
                <Button
                  size="small"
                  onClick={() => handleSetItemHorses(editingItemIndex, horses.map((h) => h.id))}
                >
                  All
                </Button>
                <Button size="small" onClick={() => handleSetItemHorses(editingItemIndex, [])}>
                  None
                </Button>
              </Stack>
            </Stack>
            {horses.map((horse) => (
              <Box
                key={horse.id}
                sx={{ display: 'flex', alignItems: 'center', gap: 0.5, cursor: 'pointer' }}
                onClick={() =>
                  handleToggleItemHorse(
                    editingItemIndex,
                    horse.id,
                    !items[editingItemIndex].horseIds.includes(horse.id),
                  )
                }
              >
                <Checkbox
                  size="small"
                  checked={items[editingItemIndex].horseIds.includes(horse.id)}
                  sx={{ p: 0.5 }}
                />
                <Typography variant="body2">{horse.name}</Typography>
              </Box>
            ))}
          </Box>
        )}
      </Popover>

      {/* Shared Settings & Invoice Options — keyed off items, not the
          selection: items carry their own horses now. */}
      {items.length > 0 && (
        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" fontWeight={600} mb={2}>
            Visit Details &amp; Invoice Options
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={3}>
              <TextField
                label="Service Date"
                type="date"
                size="small"
                fullWidth
                required
                value={sharedData.date}
                onChange={(e) => handleServiceDateChange(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={3}>
              <Autocomplete
                options={veterinarians}
                getOptionLabel={(option) =>
                  typeof option === 'string' ? option : option.name
                }
                freeSolo
                inputValue={sharedData.performedBy}
                onInputChange={(_, value) =>
                  setSharedData((prev) => ({ ...prev, performedBy: value }))
                }
                renderInput={(params) => (
                  <TextField {...params} label="Performed By" size="small" />
                )}
              />
            </Grid>
            <Grid item xs={12} sm={3}>
              <TextField
                label="Invoice Due Date"
                type="date"
                size="small"
                fullWidth
                value={sharedData.dueDate}
                onChange={(e) =>
                  setSharedData((prev) => ({
                    ...prev,
                    dueDate: e.target.value,
                  }))
                }
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={3}>
              <TextField
                label="Invoice Description"
                size="small"
                fullWidth
                placeholder="Optional"
                value={sharedData.invoiceDescription}
                onChange={(e) =>
                  setSharedData((prev) => ({
                    ...prev,
                    invoiceDescription: e.target.value,
                  }))
                }
              />
            </Grid>
            {/* One deduction source for the whole barn run — every med/vaccine
                item deducts from it. Renders only when the clinic has trucks;
                Automatic = server infers (ADR-0013). */}
            <Grid item xs={12} sm={3}>
              <DeductFromLocationSelect
                value={dispenseLocationId}
                onChange={setDispenseLocationId}
                margin="none"
              />
            </Grid>
          </Grid>
        </Paper>
      )}

      {/* Review & Submit */}
      {horses.length > 0 && items.length > 0 && (
        <Card
          sx={{
            mb: 3,
            border: '1px solid',
            borderColor: 'divider',
          }}
        >
          <CardContent>
            <Typography variant="h6" fontWeight={600} mb={0.5}>
              Invoice Preview
            </Typography>
            <Typography variant="body2" color="text.secondary" mb={2}>
              Every horse in {selectedBarn} is shown. Each horse is billed only for the services
              assigned to it — horses with no assigned services are skipped.
            </Typography>
            <Stack spacing={2} mb={2}>
              {horses.map((horse) => {
                const entry = materialized.find((e) => e.horseId === horse.id);
                const horseItems = entry?.items ?? [];
                const horseTotal = horseItems.reduce((s, it) => s + getLineTotal(it), 0);
                return (
                <Box
                  key={horse.id}
                  sx={{
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 1,
                    overflow: 'hidden',
                    opacity: horseItems.length > 0 ? 1 : 0.55,
                  }}
                >
                  <Stack
                    direction="row"
                    justifyContent="space-between"
                    alignItems="center"
                    flexWrap="wrap"
                    sx={{ px: 2, py: 1, bgcolor: 'action.hover' }}
                  >
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <Typography variant="subtitle2" fontWeight={600}>
                        {horse.name}
                      </Typography>
                      {horseItems.length === 0 && (
                        <Chip label="No services — skipped" size="small" variant="outlined" />
                      )}
                    </Stack>
                    <Typography variant="caption" color="text.secondary">
                      Bill to:{' '}
                      {ownerLinksByHorse.has(horse.id)
                        ? ownerLinksByHorse.get(horse.id)!.map((link) => link.name).join(', ')
                        : getOwnerNames(horse)}
                    </Typography>
                  </Stack>
                  {horseItems.length > 0 && (
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Type</TableCell>
                        <TableCell>Service</TableCell>
                        <TableCell align="right">Unit Price</TableCell>
                        <TableCell align="right">Qty</TableCell>
                        <TableCell align="right">Line Total</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {horseItems.map((item, index) => {
                        const qty = getBilledQty(item);
                        const price = typeof item.unitPrice === 'number' ? item.unitPrice : 0;
                        return (
                          <TableRow key={index}>
                            <TableCell>
                              <Stack direction="row" alignItems="center" spacing={0.5}>
                                <ServiceTypeIcon type={item.serviceType} />
                                <Typography variant="body2">
                                  {SERVICE_TYPE_LABELS[item.serviceType]}
                                </Typography>
                              </Stack>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2">{item.serviceName}</Typography>
                              {item.notes && (
                                <Typography variant="caption" color="text.secondary">
                                  {item.notes}
                                </Typography>
                              )}
                            </TableCell>
                            <TableCell align="right">
                              <Typography variant="body2">
                                {price > 0 ? `$${price.toFixed(2)}` : '—'}
                              </Typography>
                            </TableCell>
                            <TableCell align="right">
                              <Typography variant="body2">{formatQuantity(qty)}</Typography>
                            </TableCell>
                            <TableCell align="right">
                              <Typography variant="body2">
                                {price > 0 ? `$${getLineTotal(item).toFixed(2)}` : '—'}
                              </Typography>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                      <TableRow>
                        <TableCell colSpan={4} align="right" sx={{ borderBottom: 'none' }}>
                          <Typography variant="body2" fontWeight={600}>
                            Invoice Total (pre-tax)
                          </Typography>
                        </TableCell>
                        <TableCell align="right" sx={{ borderBottom: 'none' }}>
                          <Typography variant="body2" fontWeight={600}>
                            ${horseTotal.toFixed(2)}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                  )}
                </Box>
                );
              })}
            </Stack>
            <Stack spacing={0.5} mb={2}>
              <Typography variant="body1" fontWeight={600} color="primary.main">
                Estimated Total: ${estimatedTotal.toFixed(2)}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Before tax. Tax will be calculated per invoice based on clinic settings.
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {materialized.length === 0
                  ? 'No services assigned — add a service for the checked horses above.'
                  : `${materialized.length} invoice${materialized.length !== 1 ? 's' : ''} will be created (more if horses have multiple owners)`}
              </Typography>
            </Stack>
            <OfflineNotice>
              You're offline — barn billing creates invoices and needs a connection. You can
              browse barns and horses; apply services when you reconnect.
            </OfflineNotice>
            <Stack direction="row" spacing={2}>
              {can.submit && (
              <Button
                variant="contained"
                onClick={handleSubmit}
                disabled={!canSubmit || isOffline}
                sx={{ minWidth: 160 }}
              >
                {barnOperationMutation.isLoading ? (
                  <CircularProgress size={24} color="inherit" />
                ) : (
                  `Apply ${items.length} Item${items.length !== 1 ? 's' : ''} · ${materialized.length} Horse${materialized.length !== 1 ? 's' : ''} Invoiced`
                )}
              </Button>
              )}
              <Button variant="outlined" onClick={handleReset}>
                Reset
              </Button>
            </Stack>
          </CardContent>
        </Card>
      )}

      {/* Success banner — line items reset on success so the user can keep
          adding services; the banner stays until dismissed or a new run. */}
      {operationResults && (
        <Alert
          severity={operationResults.summary.errorCount === 0 ? 'success' : 'warning'}
          sx={{ mb: 3 }}
          action={
            <Stack direction="row" spacing={1} alignItems="center">
              <Button
                color="inherit"
                size="small"
                variant="outlined"
                onClick={() => navigate('/billing')}
              >
                View invoices
              </Button>
              <IconButton
                size="small"
                color="inherit"
                aria-label="Dismiss"
                onClick={() => setOperationResults(null)}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </Stack>
          }
        >
          <AlertTitle>
            {operationResults.summary.totalInvoices} invoice
            {operationResults.summary.totalInvoices !== 1 ? 's' : ''} created
            {operationResults.summary.errorCount > 0
              ? ` · ${operationResults.summary.errorCount} failed`
              : ''}
            {` · $${operationResults.summary.totalAmount.toFixed(2)} total`}
          </AlertTitle>
          <Stack spacing={0.25} sx={{ mt: 0.5 }}>
            {operationResults.results.map((result) => (
              <Typography key={result.horseId} variant="body2">
                <strong>{result.horseName || result.horseId}</strong>
                {result.status === 'success'
                  ? ` — ${
                      result.invoiceNumbers.length > 0
                        ? result.invoiceNumbers.join(', ')
                        : 'no invoice'
                    } · $${result.amount.toFixed(2)}`
                  : ` — failed: ${result.error}`}
              </Typography>
            ))}
          </Stack>
        </Alert>
      )}

      <BarnVaccineLotsDialog
        open={!!lotStepPayload}
        vaccines={vaccineLines}
        locationId={dispenseLocationId}
        givenOn={sharedData.date}
        onCancel={() => setLotStepPayload(null)}
        onSkip={() => {
          const payload = lotStepPayload;
          setLotStepPayload(null);
          if (payload) barnOperationMutation.mutate(payload);
        }}
        onConfirm={(lots) => {
          const payload = lotStepPayload;
          setLotStepPayload(null);
          if (payload) barnOperationMutation.mutate({ ...payload, perHorseItems: withVaccineLots(payload.perHorseItems, lots) });
        }}
      />

      {selectedBarnRecord && (
        <AddHorsesToBarnDialog
          open={addHorsesOpen}
          onClose={() => setAddHorsesOpen(false)}
          barnId={selectedBarnRecord.id}
          barnName={selectedBarnRecord.name}
        />
      )}
    </Box>
  );
};

export default BarnOperations;
